import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { getFeed, getReviewFeed } from "../../utility/httpActions/feedActions";
import { updateUserProfile } from "../../utility/httpActions/userActions";
import ProductCard from "../../common-components/Product/ProductCard";
import useAuthentication from "../../hooks/useAuthentication";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import {
  useInfiniteShopRecommendations,
  useInfinitePeopleRecommendations,
  RECOMMENDATIONS_PAGE_SIZE,
} from "../../hooks/useFeedRecommendations";
import FeedReviewCard from "./FeedReviewCard";
import FeedShopRecommendationsRow from "./FeedShopRecommendationsRow";
import FeedPeopleRecommendationsRow from "./FeedPeopleRecommendationsRow";
import FeedNoResults from "./FeedNoResults";
import FilterPanel from "../../common-components/filters/FilterPanel";
import useCatalogFilters from "../../hooks/useCatalogFilters";
import { productSortOptions } from "../../utility/constants";

const PAGE_SIZE = 20;
const REVIEW_PAGE_SIZE = 5;
const REVIEW_EVERY_N_MIN = 2;
const REVIEW_EVERY_N_MAX = 6;
const MAX_INTERESTS = 10;

// Base recurrence periods, counted directly in products appended — shop rows
// are due more often than people rows, giving products > reviews > shops >
// people. These are just the base pacing preference: resolveSlotTypes below
// overrides them with novelty whenever a due type has nothing new to show.
// Mirrors ngwaahia-mobile's components/feed/HomeFeed.js (final form, #176/#177).
const SHOP_EVERY_N_PRODUCTS = 8;
const PEOPLE_EVERY_N_PRODUCTS = 10;
// The desktop grid has 3 columns, so its periods are multiples of 3: a full-width
// row that lands mid-row would leave a gap in the grid above it.
const DESKTOP_SHOP_EVERY_N_PRODUCTS = 9;
const DESKTOP_PEOPLE_EVERY_N_PRODUCTS = 12;

// Expand each review into one item per product rating + optional shop card for global media.
const expandReviews = (reviews) =>
  reviews.flatMap((review) => {
    const rated = (review.productRatings || []).filter((pr) => pr.rating > 0);
    const hasGlobalMedia = (review.media || []).length > 0;
    if (rated.length > 0) {
      const productItems = rated.map((pr) => ({
        type: "review",
        key: `r_${review._id}_${pr.product}`,
        data: { ...review, _featuredProductRating: pr },
      }));
      const shopItem = hasGlobalMedia
        ? [{ type: "review", key: `r_${review._id}_shop`, data: review }]
        : [];
      return [...productItems, ...shopItem];
    }
    return [{ type: "review", key: `r_${review._id}_shop`, data: review }];
  });

// How many products between each review insertion, based on actual data counts.
// Products are always priority; ratio is clamped so reviews never dominate.
const computeReviewRatio = (productCount, reviewCount) => {
  if (reviewCount === 0 || productCount === 0) return REVIEW_EVERY_N_MAX;
  return Math.max(
    REVIEW_EVERY_N_MIN,
    Math.min(REVIEW_EVERY_N_MAX, Math.ceil(productCount / reviewCount)),
  );
};

// A type is 'ready' if there's an already-fetched, not-yet-shown page for it,
// 'pending' if more is coming but hasn't arrived yet (try again at a later
// slot rather than showing stale content), or 'exhausted' once there's
// genuinely nothing left (only then is repeating it acceptable).
const getRecStatus = (occurrence, pages, hasNextPage) => {
  if (occurrence < pages.length) return "ready";
  return hasNextPage ? "pending" : "exhausted";
};

// Novelty-first slot resolution: a due type shows its own fresh page if it
// has one. If it's exhausted but the OTHER type still has fresh content,
// that's shown instead of repeating. Only once a type is exhausted with
// nothing to borrow does a slot recycle (repeat) — pending slots are simply
// skipped rather than repeated.
const pickForSlot = (due, ownStatus, otherStatus, ownType, otherType) => {
  if (!due) return null;
  if (ownStatus === "ready") return ownType;
  if (otherStatus === "ready") return otherType;
  if (ownStatus === "exhausted") return ownType;
  return null;
};

const resolveSlotTypes = (shopDue, peopleDue, shopStatus, peopleStatus) => {
  const shopPick = pickForSlot(
    shopDue,
    shopStatus,
    peopleStatus,
    "shop",
    "people",
  );
  const peoplePick = pickForSlot(
    peopleDue,
    peopleStatus,
    shopStatus,
    "people",
    "shop",
  );
  const types = [];
  if (shopPick) types.push(shopPick);
  if (peoplePick && peoplePick !== shopPick) types.push(peoplePick);
  return types;
};

// Resolves the Nth occurrence of a shop/people recommendation row to actual
// data: an already-fetched, not-yet-shown page is genuinely new content;
// once the pool is exhausted, recycle by chunking everything fetched so far
// and cycling through it via modulo.
const resolveRecRow = (occurrence, pages, pool) => {
  if (occurrence < pages.length) return pages[occurrence];
  if (!pool.length) return [];
  const numChunks = Math.ceil(pool.length / RECOMMENDATIONS_PAGE_SIZE);
  const chunkIndex = occurrence % numChunks;
  return pool.slice(
    chunkIndex * RECOMMENDATIONS_PAGE_SIZE,
    (chunkIndex + 1) * RECOMMENDATIONS_PAGE_SIZE,
  );
};

const buildRecRowItem = (kind, occurrence, recs) => {
  const items = resolveRecRow(occurrence, recs.pages, recs.pool);
  if (!items.length) return null;
  return {
    type: kind === "shop" ? "shopRecommendations" : "peopleRecommendations",
    key: `${kind}_recs_${occurrence}`,
    items,
  };
};

// Builds the full interleaved item list from scratch each time. Unlike the
// mobile app's FlashList feed, this infinite query never evicts old pages,
// so `products`/`expandedReviews` only ever grow — a full recompute is safe
// and deterministic (no mutable cursor/seen-id bookkeeping needed).
// `expandedReviews` is passed as [] for the desktop grid view, which keeps
// reviews in their own tab and only interleaves shop/people rows.
const buildInterleavedFeed = ({
  products,
  expandedReviews,
  isLoggedIn,
  shopRecs,
  peopleRecs,
  shopEveryN = SHOP_EVERY_N_PRODUCTS,
  peopleEveryN = PEOPLE_EVERY_N_PRODUCTS,
}) => {
  const items = [];
  const reviewQueue = expandedReviews.slice();
  const reviewEveryN = computeReviewRatio(
    products.length,
    expandedReviews.length,
  );

  let totalProductsAppended = 0;
  let shopOccurrence = 0;
  let peopleOccurrence = 0;

  const pushRecRow = (kind) => {
    const isShop = kind === "shop";
    if (!isShop && !isLoggedIn) return;
    const occurrence = isShop ? shopOccurrence : peopleOccurrence;
    const recs = isShop ? shopRecs : peopleRecs;
    const item = buildRecRowItem(kind, occurrence, recs);
    if (!item) return;
    items.push(item);
    if (isShop) shopOccurrence += 1;
    else peopleOccurrence += 1;
  };

  products.forEach((product) => {
    const id = product._id ?? product.id;
    items.push({ type: "product", key: `p_${id}`, data: product });
    totalProductsAppended += 1;

    // Review insertion and shop/people insertion are independent periodic
    // checks against the same running product count — not nested inside one
    // another — so shop/people recurrence doesn't inherit review cadence's
    // sparsity.
    if (totalProductsAppended % reviewEveryN === 0 && reviewQueue.length) {
      items.push(reviewQueue.shift());
    }

    const shopDue = totalProductsAppended % shopEveryN === 0;
    const peopleDue = isLoggedIn && totalProductsAppended % peopleEveryN === 0;
    if (!shopDue && !peopleDue) return;

    const shopStatus = getRecStatus(
      shopOccurrence,
      shopRecs.pages,
      shopRecs.hasNextPage,
    );
    const peopleStatus = isLoggedIn
      ? getRecStatus(peopleOccurrence, peopleRecs.pages, peopleRecs.hasNextPage)
      : "exhausted";
    resolveSlotTypes(shopDue, peopleDue, shopStatus, peopleStatus).forEach(
      pushRecRow,
    );
  });

  return { items, shopOccurrence, peopleOccurrence };
};

// Guarantees at least one shop row and (if logged in) one people row, even on
// a feed short enough that the natural cadence never reached either — only
// once the product feed is fully loaded (nothing left to potentially reach
// the cadence with).
const withGuaranteedRows = (
  result,
  { hasNextPage, isFetchingNextPage, shopRecs, peopleRecs, isLoggedIn },
) => {
  if (hasNextPage || isFetchingNextPage) return result.items;
  const guaranteed = [];
  if (result.shopOccurrence === 0) {
    const shopItem = buildRecRowItem("shop", 0, shopRecs);
    if (shopItem) guaranteed.push(shopItem);
  }
  if (isLoggedIn && result.peopleOccurrence === 0) {
    const peopleItem = buildRecRowItem("people", 0, peopleRecs);
    if (peopleItem) guaranteed.push(peopleItem);
  }
  return guaranteed.length ? [...result.items, ...guaranteed] : result.items;
};

// Fallback shown when there are no products at all yet (e.g. a brand-new/
// heavily filtered feed): still surface a shop row and (if logged in) a
// people row above the empty message, instead of a totally blank section.
const buildEmptyFeedFallback = ({ shopRecs, peopleRecs, isLoggedIn }) => {
  const items = [];
  const shopItem = buildRecRowItem("shop", 0, shopRecs);
  if (shopItem) items.push(shopItem);
  if (isLoggedIn) {
    const peopleItem = buildRecRowItem("people", 0, peopleRecs);
    if (peopleItem) items.push(peopleItem);
  }
  return items;
};

// Split a merged item list into product batches (rendered as a grid) and
// single full-width items (review cards, shop/people recommendation rows)
// for rendering.
const buildSegments = (items) => {
  const segments = [];
  let batch = [];
  items.forEach((item) => {
    if (item.type === "product") {
      batch.push(item);
    } else {
      if (batch.length > 0) {
        segments.push({ type: "products", items: batch });
        batch = [];
      }
      segments.push({ type: item.type, item });
    }
  });
  if (batch.length > 0) segments.push({ type: "products", items: batch });
  return segments;
};

const renderSegment = (seg, i) => {
  if (seg.type === "review") {
    return <FeedReviewCard key={seg.item.key} review={seg.item.data} />;
  }
  if (seg.type === "shopRecommendations") {
    return (
      <FeedShopRecommendationsRow key={seg.item.key} shops={seg.item.items} />
    );
  }
  if (seg.type === "peopleRecommendations") {
    return (
      <FeedPeopleRecommendationsRow
        key={seg.item.key}
        people={seg.item.items}
      />
    );
  }
  return (
    <Grid container spacing={{ xs: 2, md: 2.5 }} key={i}>
      {seg.items.map((item) => (
        <Grid item xs={6} sm={4} key={item.key}>
          <ProductCard product={item.data} roomy />
        </Grid>
      ))}
    </Grid>
  );
};

const ProductsFeed = () => {
  const sentinelRef = useRef(null);
  const { currentUser, isLoggedIn, setCurrentUser } = useAuthentication();
  const userWantsReviews = currentUser?.feedPreferences?.showReviews !== false;
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const [activeTab, setActiveTab] = useState(0);

  const filters = useCatalogFilters();
  const {
    selectedCategories,
    selectedDeliveryMethods,
    selectedPaymentMethods,
    selectedPaymentTimings,
    locationCountry,
    locationState,
    toggleCategory,
    clearAll,
  } = filters;
  // null = the feed's own personalised ordering. A chosen sort leads and that
  // ordering only breaks ties (see getFeed in the backend's controllers/feed.js).
  const [sort, setSort] = useState(null);

  // Only defined keys: getFeed feeds this straight into URLSearchParams, which
  // would serialise an undefined value as the string "undefined".
  const feedFilters = useMemo(() => {
    const csv = (list) => list.slice().sort().join(",");
    const params = {};
    if (selectedCategories.length) params.categories = csv(selectedCategories);
    if (selectedDeliveryMethods.length)
      params.deliveryMethods = csv(selectedDeliveryMethods);
    if (selectedPaymentMethods.length)
      params.paymentMethods = csv(selectedPaymentMethods);
    if (selectedPaymentTimings.length)
      params.paymentTimings = csv(selectedPaymentTimings);
    if (locationState) params.state = locationState;
    if (locationCountry) params.country = locationCountry;
    if (sort) params.sort = sort;
    return params;
  }, [
    selectedCategories,
    selectedDeliveryMethods,
    selectedPaymentMethods,
    selectedPaymentTimings,
    locationState,
    locationCountry,
    sort,
  ]);

  const reviewFeedEnabled = useFeatureFlag("review-feed");
  const showReviews = reviewFeedEnabled && userWantsReviews;

  useEffect(() => {
    if (activeTab === 1 && !showReviews) setActiveTab(0);
  }, [showReviews, activeTab]);

  const {
    data: feedData,
    isLoading: feedLoading,
    // True while a changed filter/sort is fetching and the previous results are
    // still on screen (see placeholderData below).
    isPlaceholderData: feedFiltering,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["homeFeed", feedFilters],
    queryFn: ({ pageParam = 1 }) =>
      getFeed({ page: pageParam, limit: PAGE_SIZE, ...feedFilters }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    // Keep the old grid visible while a filter/sort change loads, instead of
    // swapping it for a full-page spinner (the filter header shows a small one).
    placeholderData: (previousData) => previousData,
  });

  // Tapping a category the user doesn't already have as an interest silently
  // grows their interests array too (capped at MAX_INTERESTS), so future feed
  // ranking benefits from it. Reads/writes pendingInterestsRef rather than
  // currentUser.interests directly so two rapid clicks don't both read the
  // same stale snapshot and clobber each other via the full-replace PATCH.
  const pendingInterestsRef = useRef(currentUser?.interests || []);
  useEffect(() => {
    pendingInterestsRef.current = currentUser?.interests || [];
  }, [currentUser?.interests]);

  const { mutate: updateProfile } = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data) => {
      if (data?.user) setCurrentUser(data.user);
    },
    onError: () => {
      pendingInterestsRef.current = currentUser?.interests || [];
    },
  });

  const handleToggleCategory = useCallback(
    (value) => {
      const isSelecting = !selectedCategories.includes(value);
      toggleCategory(value);
      if (isSelecting && isLoggedIn) {
        const base = pendingInterestsRef.current;
        if (!base.includes(value) && base.length < MAX_INTERESTS) {
          const next = [...base, value];
          pendingInterestsRef.current = next;
          updateProfile({ interests: next });
        }
      }
    },
    [selectedCategories, toggleCategory, isLoggedIn, updateProfile],
  );

  // Category taps go through handleToggleCategory so they also grow the user's
  // interests; everything else on the panel uses the hook's handlers as-is.
  const panelFilters = { ...filters, toggleCategory: handleToggleCategory };

  const handleClearFilters = useCallback(() => {
    clearAll();
    setSort(null);
  }, [clearAll]);

  // Tapping the active sort again returns to the feed's default ordering.
  const handleSortChange = useCallback(
    (value) => setSort((prev) => (prev === value ? null : value)),
    [],
  );

  const {
    data: reviewData,
    fetchNextPage: fetchNextReviewPage,
    hasNextPage: hasNextReviewPage,
    isFetchingNextPage: isFetchingNextReviewPage,
  } = useInfiniteQuery({
    queryKey: ["reviewFeed"],
    queryFn: ({ pageParam = 1 }) =>
      getReviewFeed({ page: pageParam, limit: REVIEW_PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.totalPages
        ? lastPage.currentPage + 1
        : undefined,
    enabled: showReviews,
  });

  const {
    data: shopRecData,
    fetchNextPage: fetchNextShopRecPage,
    hasNextPage: hasNextShopRecPage,
    isFetchingNextPage: isFetchingNextShopRecPage,
  } = useInfiniteShopRecommendations();

  const {
    data: peopleRecData,
    fetchNextPage: fetchNextPeopleRecPage,
    hasNextPage: hasNextPeopleRecPage,
    isFetchingNextPage: isFetchingNextPeopleRecPage,
  } = useInfinitePeopleRecommendations();

  // The placeholder pages belong to the previous filters; paging them would
  // request a next page of results the new filters never asked for.
  const canFetchNextFeedPage =
    hasNextPage && !isFetchingNextPage && !feedFiltering;

  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (activeTab === 0 && canFetchNextFeedPage) fetchNextPage();
          if (activeTab === 1 && hasNextReviewPage && !isFetchingNextReviewPage)
            fetchNextReviewPage();
          // mobile merged feed fetches everything
          if (!isDesktop) {
            if (canFetchNextFeedPage) fetchNextPage();
            if (hasNextReviewPage && !isFetchingNextReviewPage)
              fetchNextReviewPage();
          }
          if (hasNextShopRecPage && !isFetchingNextShopRecPage)
            fetchNextShopRecPage();
          if (hasNextPeopleRecPage && !isFetchingNextPeopleRecPage)
            fetchNextPeopleRecPage();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [
    activeTab,
    isDesktop,
    canFetchNextFeedPage,
    fetchNextPage,
    hasNextReviewPage,
    isFetchingNextReviewPage,
    fetchNextReviewPage,
    hasNextShopRecPage,
    isFetchingNextShopRecPage,
    fetchNextShopRecPage,
    hasNextPeopleRecPage,
    isFetchingNextPeopleRecPage,
    fetchNextPeopleRecPage,
  ]);

  const products = useMemo(
    () => feedData?.pages?.flatMap((page) => page.products) ?? [],
    [feedData],
  );

  const expandedReviews = useMemo(
    () =>
      showReviews
        ? expandReviews(
            reviewData?.pages?.flatMap((page) => page.reviews) ?? [],
          )
        : [],
    [showReviews, reviewData],
  );

  const shopRecs = useMemo(() => {
    const pages = shopRecData?.pages?.map((page) => page.shops) ?? [];
    return { pages, pool: pages.flat(), hasNextPage: !!hasNextShopRecPage };
  }, [shopRecData, hasNextShopRecPage]);

  const peopleRecs = useMemo(() => {
    const pages = peopleRecData?.pages?.map((page) => page.people) ?? [];
    return { pages, pool: pages.flat(), hasNextPage: !!hasNextPeopleRecPage };
  }, [peopleRecData, hasNextPeopleRecPage]);

  // Desktop grid: products + interleaved shop/people rows only (reviews stay
  // in their own tab).
  const desktopSegments = useMemo(() => {
    if (!isDesktop || products.length === 0) return [];
    const result = buildInterleavedFeed({
      products,
      expandedReviews: [],
      isLoggedIn,
      shopRecs,
      peopleRecs,
      shopEveryN: DESKTOP_SHOP_EVERY_N_PRODUCTS,
      peopleEveryN: DESKTOP_PEOPLE_EVERY_N_PRODUCTS,
    });
    const items = withGuaranteedRows(result, {
      hasNextPage,
      isFetchingNextPage,
      shopRecs,
      peopleRecs,
      isLoggedIn,
    });
    return buildSegments(items);
  }, [
    isDesktop,
    products,
    isLoggedIn,
    shopRecs,
    peopleRecs,
    hasNextPage,
    isFetchingNextPage,
  ]);

  // Mobile-width: single interleaved list of products, reviews, and rows.
  const mobileSegments = useMemo(() => {
    if (isDesktop || products.length === 0) return [];
    const result = buildInterleavedFeed({
      products,
      expandedReviews,
      isLoggedIn,
      shopRecs,
      peopleRecs,
    });
    const items = withGuaranteedRows(result, {
      hasNextPage,
      isFetchingNextPage,
      shopRecs,
      peopleRecs,
      isLoggedIn,
    });
    return buildSegments(items);
  }, [
    isDesktop,
    products,
    expandedReviews,
    isLoggedIn,
    shopRecs,
    peopleRecs,
    hasNextPage,
    isFetchingNextPage,
  ]);

  const emptyFeedFallback = useMemo(
    () =>
      products.length > 0
        ? []
        : buildSegments(
            buildEmptyFeedFallback({ shopRecs, peopleRecs, isLoggedIn }),
          ),
    [products, shopRecs, peopleRecs, isLoggedIn],
  );

  // With filters on, the no-results message goes first so it answers the
  // filter before the (unfiltered) recommendations; for a genuinely empty
  // marketplace the "no products yet" note stays below them.
  const emptyFeed = filters.hasActiveFilters ? (
    <Box>
      <FeedNoResults />
      {emptyFeedFallback.map(renderSegment)}
    </Box>
  ) : (
    <Box>
      {emptyFeedFallback.map(renderSegment)}
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography color="text.secondary">No products yet.</Typography>
      </Box>
    </Box>
  );

  // ── Desktop: tabbed Products / Reviews ───────────────────────────────────
  if (isDesktop) {
    const isFetchingTab =
      (activeTab === 0 && isFetchingNextPage) ||
      (activeTab === 1 && isFetchingNextReviewPage);

    return (
      <Box>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            mb: 2,
            borderBottom: "1px solid #e5e7eb",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              color: "#6b7280",
            },
            "& .Mui-selected": { color: "#046287" },
            "& .MuiTabs-indicator": { backgroundColor: "#046287" },
          }}
        >
          <Tab label="Products" />
          {showReviews && <Tab label="Reviews" />}
        </Tabs>

        {activeTab === 0 && (
          <>
            <FilterPanel
              filters={panelFilters}
              sortOptions={productSortOptions}
              sortValue={sort}
              onSortChange={handleSortChange}
              hasActiveSort={!!sort}
              onClearAll={handleClearFilters}
              isFiltering={feedFiltering}
            />
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        {activeTab === 0 &&
          (feedLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#046287" }} />
            </Box>
          ) : products.length === 0 ? (
            emptyFeed
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, md: 2.5 },
              }}
            >
              {desktopSegments.map(renderSegment)}
            </Box>
          ))}

        {activeTab === 1 &&
          (expandedReviews.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography color="text.secondary">No reviews yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {expandedReviews.map((item) => (
                <FeedReviewCard key={item.key} review={item.data} />
              ))}
            </Box>
          ))}

        <Box
          ref={sentinelRef}
          sx={{ py: 4, display: "flex", justifyContent: "center" }}
        >
          {isFetchingTab && (
            <CircularProgress size={28} sx={{ color: "#046287" }} />
          )}
        </Box>
      </Box>
    );
  }

  // ── Mobile: single interleaved feed ──────────────────────────────────────
  return (
    <Box>
      <Typography
        variant="subtitle1"
        fontWeight={700}
        sx={{ mb: 2, color: "#111827" }}
      >
        Products
      </Typography>

      <FilterPanel
        filters={panelFilters}
        sortOptions={productSortOptions}
        sortValue={sort}
        onSortChange={handleSortChange}
        hasActiveSort={!!sort}
        onClearAll={handleClearFilters}
        isFiltering={feedFiltering}
      />
      <Divider sx={{ mb: 2 }} />

      {feedLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: "#046287" }} />
        </Box>
      ) : products.length === 0 ? (
        emptyFeed
      ) : (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {mobileSegments.map(renderSegment)}
          </Box>
          <Box
            ref={sentinelRef}
            sx={{ py: 4, display: "flex", justifyContent: "center" }}
          >
            {(isFetchingNextPage || isFetchingNextReviewPage) && (
              <CircularProgress size={28} sx={{ color: "#046287" }} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProductsFeed;
